import { useRef, useState } from "react";
import {
  Link,
  useNavigate,
  useParams,
  useSearchParams,
} from "react-router-dom";
import {
  ProTable,
  type ActionType,
  type ProColumns,
} from "@ant-design/pro-components";
import {
  App,
  Alert,
  Button,
  Card,
  Empty,
  Image,
  List,
  Modal,
  Space,
  Select,
  Tabs,
  Tag,
  Timeline,
  Typography,
} from "antd";
import {
  ArrowLeftOutlined,
  ArrowRightOutlined,
  EditOutlined,
  EyeOutlined,
  FileTextOutlined,
  PlusOutlined,
  SendOutlined,
} from "@ant-design/icons";
import { DetailSurface, DetailSection, DetailFields } from "./detail-layout";
import { TaskSubmissions } from "./submissions";
import { MaterialImage } from "./materials";
import { MessageImport } from "./message-import";
import { TaskFinancePanel } from "./finance";
import { taskBudgetMinor } from "./finance-domain";
import { checkTaskFunding, TaskFundingCheck } from "./finance-funding";
import { TaskPreview } from "./task-preview";
import { revisionAuthor, TaskDiff } from "./task-diff";
import { api, post, query } from "./api";
import { businessTaskTypes, taskBasePath, taskPath, taskBusiness, type TaskBusiness } from "./business";
import {
  dateTime,
  money,
  isTaskClosed,
  executionLabels,
  typeLabels,
  type Agent,
  type Page,
  type Revision,
  type Review,
  type Task,
} from "./domain";
import {
  RefreshButton,
  Resource,
  SectionTitle,
  ExecutionTag,
  useResource,
} from "./shared";
export function Tasks({ agent, business }: { agent: Agent; business: TaskBusiness }) {
  return <TasksList key={business} agent={agent} business={business} />;
}
function TasksList({ agent, business }: { agent: Agent; business: TaskBusiness }) {
  const label = business === "sms" ? "iMessage 任务" : "App 推广任务";
  const allowed = businessTaskTypes(agent, business);
  const ref = useRef<ActionType>(undefined);
  const [params, setParams] = useSearchParams();
  const [error, setError] = useState("");
  const status = params.get("execution_status") || params.get("status") || "";
  const [counts, setCounts] = useState<Record<string, number>>({});
  const columns: ProColumns<Task>[] = [
    {
      title: "任务名称",
      dataIndex: "search",
      width: 310,
      render: (_, r) => (
        <div className="task-cell">
          <span className={`task-type-icon ${r.payload.task_type}`}>
            <FileTextOutlined />
          </span>
          <div>
            <Link to={taskPath(business, r.id)}>{r.payload.title}</Link>
          </div>
        </div>
      ),
    },
    {
      title: "任务类型",
      dataIndex: "task_type",
      hideInSearch: business === "sms",
      hideInTable: business === "sms",
      valueType: "select",
      valueEnum: Object.fromEntries(allowed.map(type => [type, { text: typeLabels[type] }])),
      render: (_, r) => (
        <Tag bordered={false}>{typeLabels[r.payload.task_type]}</Tag>
      ),
    },
    {
      title: "单次奖励",
      dataIndex: "reward",
      hideInSearch: true,
      align: "right",
      render: (_, r) => (
        <span className="money-cell">
          {money(r.payload.reward_amount_minor)}
        </span>
      ),
    },
    {
      title: "任务数量",
      dataIndex: "quantity",
      hideInSearch: true,
      align: "right",
      render: (_, r) => r.payload.max_assignments || "待填写",
    },
    {
      title: "状态",
      dataIndex: "status",
      hideInSearch: true,
      render: (_, r) => <ExecutionTag status={r.execution_status || r.status} />,
    },
    {
      title: "更新时间",
      dataIndex: "updated_at",
      hideInSearch: true,
      render: (_, r) => dateTime(r.updated_at),
    },
    {
      title: "操作",
      valueType: "option",
      width: 150,
      render: (_, r) => [
        <Link key="detail" to={taskPath(business, r.id)}>
          查看
        </Link>,
        ...(r.status !== "pending_review" && !isTaskClosed(r)
          ? [
              <Link key="edit" to={taskPath(business, r.id, true)}>
                编辑
              </Link>,
            ]
          : r.status === "pending_review" ? [<Link key="withdraw" to={taskPath(business, r.id)}>撤回修改</Link>] : []),
      ],
    },
  ];
  return (
    <>
      <SectionTitle
        eyebrow="TASK MANAGEMENT"
        title={label}
        description={`创建、提交和追踪${label}。提交时冻结预算，任务结束后按有效完成数量结算。`}
        action={
          <Link to={`${taskBasePath(business)}/new`}>
            <Button
              icon={<PlusOutlined />}
              type="primary"
              disabled={agent.business_frozen}
            >
              新建{label}
            </Button>
          </Link>
        }
      />
      <Card className="task-table-card">
        {error && (
          <Alert
            type="error"
            showIcon
            message="任务列表加载失败"
            description={error}
            action={<Button onClick={() => ref.current?.reload()}>重试</Button>}
            style={{ marginBottom: 20 }}
          />
        )}
        <ProTable<Task>
          actionRef={ref}
          rowKey="id"
          columns={columns}
          params={{ business, execution_status: status }}
          request={async (p) => {
            setError("");
            try {
              const result = await api<Page<Task>>(
                `/tasks${query({ business, page: p.current, page_size: p.pageSize, search: p.search, task_type: business === "sms" ? "message_send" : p.task_type, execution_status: status })}`,
              );
              setCounts(result.execution_counts || {});
              return { data: result.items, total: result.total, success: true };
            } catch (e) {
              setCounts({});
              setError((e as Error).message);
              return { data: [], total: 0, success: false };
            }
          }}
          search={{
            labelWidth: "auto",
            defaultCollapsed: false,
            searchText: "查询",
            resetText: "重置",
          }}
          toolbar={{
            menu: {
              type: "tab",
              activeKey: status,
              items: [
                { key: "", label: `全部${label}${counts.all === undefined ? "" : ` ${counts.all}`}` },
                ...Object.entries(executionLabels).map(([key, label]) => ({
                  key,
                  label: `${label}${counts[key] === undefined ? "" : ` ${counts[key]}`}`,
                })),
              ],
              onChange: (key) => {
                ref.current?.setPageInfo?.({ current: 1 });
                setParams(key ? { execution_status: String(key) } : {});
              },
            },
          }}
          options={{
            density: true,
            fullScreen: true,
            reload: true,
            setting: true,
          }}
          pagination={{
            pageSize: 10,
            showSizeChanger: true,
            showTotal: (total) => `共 ${total} 个任务`,
          }}
          scroll={{ x: 1000 }}
          locale={{
            emptyText: error ? (
              <span>请重试加载任务列表</span>
            ) : (
              <Empty description={status ? `当前状态下暂无${label}` : `暂无${label}，开始创建第一个任务吧`}>
                <Link to={`${taskBasePath(business)}/new`}>
                  <Button type="primary" disabled={agent.business_frozen}>
                    创建{label}
                  </Button>
                </Link>
              </Empty>
            ),
          }}
        />
      </Card>
    </>
  );
}
export function TaskDetail({ agent, business }: { agent: Agent; business: TaskBusiness }) {
  const { id } = useParams();
  return <TaskDetailContent key={`${business}:${id}`} agent={agent} business={business} id={id} />;
}
function TaskDetailContent({ agent, business, id }: { agent: Agent; business: TaskBusiness; id?: string }) {
  const resource = useResource<Task>(`/tasks/${id}${query({ business })}`);
  const [params, setParams] = useSearchParams();
  const { message, modal } = App.useApp();
  const [busy, setBusy] = useState(false);
  const navigate = useNavigate();
  const action = async (status: "submit" | "withdraw" | "paused" | "offline") => {
    if (!resource.data || taskBusiness(resource.data.payload.task_type) !== business) return;
    setBusy(true);
    try {
      if (status === "submit") await checkTaskFunding(taskBudgetMinor(resource.data.payload.reward_amount_minor, resource.data.payload.max_assignments), !!resource.data.formal_task_id);
      if (status === "submit" || status === "withdraw")
        await post(`/tasks/${id}/${status}${query({ business })}`, {
          expected_version: resource.data.version,
        });
      else
        await post(`/tasks/${id}/status${query({ business })}`, {
          expected_version: resource.data.version,
          status,
          reason: status === "paused" ? "商户主动暂停" : "商户主动下架",
        });
      message.success(
        status === "submit" ? "已提交平台审核，任务预算已预留" : status === "withdraw" ? "已撤回，可修改后再次提交" : "任务状态已更新",
      );
      await resource.reload();
      if (status === "withdraw") navigate(taskPath(business, Number(id), true));
    } catch (e) {
      message.error((e as Error).message);
      await resource.reload();
    } finally {
      setBusy(false);
    }
  };
  return (
    <DetailSurface className="merchant-task-detail">
      <Button
        type="text"
        icon={<ArrowLeftOutlined />}
        onClick={() => navigate(taskBasePath(business))}
        style={{ paddingLeft: 0, marginBottom: 12 }}
      >
        返回{business === "sms" ? "iMessage 任务" : "App 推广任务"}列表
      </Button>
      <Resource resource={resource}>
        {(task) => taskBusiness(task.payload.task_type) !== business ? (
          <Alert showIcon type="warning" message="此任务不属于当前业务" description="请返回当前业务的任务列表。" />
        ) : (
          <>
            <SectionTitle
              eyebrow={`任务详情 · 版本 ${task.version}`}
              title={task.payload.title}
              description={`创建于 ${dateTime(task.created_at)} · 最近更新 ${dateTime(task.updated_at)}`}
              action={
                <>
                  <ExecutionTag status={task.execution_status || task.status} />
                  <RefreshButton onClick={resource.reload} />
                  {task.status === "pending_review" && <Button loading={busy} disabled={agent.business_frozen} onClick={() => modal.confirm({title:"撤回审核并修改？",content:task.formal_task_id ? "撤回后恢复为草稿，已发布任务仍保留冻结预算。" : "撤回后恢复为草稿，已冻结预算将退回可用余额。",okText:"撤回修改",cancelText:"取消",onOk:()=>action("withdraw")})}>撤回修改</Button>}
                  {task.status !== "pending_review" && !isTaskClosed(task) && (
                    <Link to={taskPath(business, task.id, true)}>
                      <Button
                        icon={<EditOutlined />}
                        disabled={agent.business_frozen}
                      >
                        编辑任务
                      </Button>
                    </Link>
                  )}
                  {["draft", "rejected", "paused", "offline"].includes(
                    task.status,
                  ) && !isTaskClosed(task) && (
                    <Button
                      type="primary"
                      loading={busy}
                      icon={<SendOutlined />}
                      disabled={
                        agent.business_frozen || agent.publish_restricted
                      }
                      onClick={() =>
                        modal.confirm({
                          title: "提交平台审核",
                          width: 820,
                          content: <><p>提交后进入平台审核；任务预算将预留至最终结算。</p><TaskFundingCheck budgetMinor={taskBudgetMinor(task.payload.reward_amount_minor, task.payload.max_assignments)} alreadyFrozen={!!task.formal_task_id} /><div style={{ maxHeight: "65vh", overflowY: "auto" }}><TaskPreview payload={task.payload} assetURLs={task.asset_urls} /></div></>,
                          okText: "确认提交",
                          cancelText: "取消",
                          onOk: () => action("submit"),
                        })
                      }
                    >
                      提交审核
                    </Button>
                  )}
                  {task.status === "approved" && task.execution_status !== "paused" && task.execution_status !== "ended" && task.execution_status !== "settled" && (
                    <Button
                      loading={busy}
                      onClick={() =>
                        modal.confirm({
                          title: "暂停此任务？",
                          content:
                            "暂停后停止新的领取，冻结预算继续保留；已有领取按原规则执行。",
                          okText: "确认暂停",
                          onOk: () => action("paused"),
                        })
                      }
                    >
                      暂停
                    </Button>
                  )}
                  {["approved", "paused"].includes(task.status) && (
                    <Button
                      danger
                      loading={busy}
                      onClick={() =>
                        modal.confirm({
                          title: "下架此任务？",
                          content:
                            "下架后停止新的领取，任务不能重新开启。已有领取及待审核记录处理完后，结算费用并解冻剩余预算。",
                          okText: "确认下架",
                          okButtonProps: { danger: true },
                          onOk: () => action("offline"),
                        })
                      }
                    >
                      下架
                    </Button>
                  )}
                </>
              }
            />
            {task.status === "pending_review" && (
              <Alert
                showIcon
                type="info"
                message="任务正在等待平台审核"
                description="如需修改，可先撤回审核，修改后再次提交。"
                style={{ marginBottom: 24 }}
              />
            )}
            <LatestReview business={business} taskId={task.id} revisionId={task.current_revision_id} />
            <RowSummary task={task} />
            {task.execution && <Card className="merchant-execution" size="small"><DetailFields size="small" column={{ xs: 2, sm: 3, lg: 6 }} items={[
              { key: "completed", label: "已完成", children: `${task.execution.completed_count} 份` },
              { key: "remaining", label: "剩余待完成", children: `${task.execution.remaining_count} 份` },
              { key: "flight", label: "执行中", children: `${task.execution.in_flight_count} 份` },
              { key: "pending", label: "待审核", children: `${task.execution.pending_review_count} 份` },
              { key: "credited", label: "奖励已发放", children: `${task.execution.reward_credited_count} 份` },
              { key: "reward", label: "奖励待发放", children: `${task.execution.reward_pending_count} 份` },
            ]} /><p className="muted" style={{ fontSize: 12, marginBottom: 0 }}>以上为会员任务执行与奖励进度；商户扣款和余额解冻见下方任务资金。</p></Card>}

            <TaskFinancePanel taskID={task.id} refreshKey={task.version} />
            <Tabs
              activeKey={params.get("tab") || "content"}
              onChange={(tab) => setParams({ tab })}
              items={[
                {
                  key: "content",
                  label: "任务内容",
                  children: <TaskContent task={task} />,
                },
                {
                  key: "history",
                  label: "修改与审核记录",
                  children: <TaskHistory business={business} taskId={task.id} />,
                },
                {
                  key: "results",
                  label: "执行结果",
                  children: <TaskSubmissions business={business} taskId={task.id} />,
                },
              ]}
            />
          </>
        )}
      </Resource>
    </DetailSurface>
  );
}
function RowSummary({ task }: { task: Task }) {
  return (
    <div className="detail-summary">
      <div>
        <span>任务类型</span>
        <strong>{task.payload.task_type === "message_send" ? "iMessage 任务" : typeLabels[task.payload.task_type]}</strong>
      </div>
      <div>
        <span>单次奖励</span>
        <strong>{money(task.payload.reward_amount_minor)}</strong>
      </div>
      <div>
        <span>目标数量</span>
        <strong>
          {task.payload.max_assignments ? `${task.payload.max_assignments.toLocaleString()} 份` : "待填写名额"}
        </strong>
      </div>
      <div>
        <span>计划奖励预算</span>
        <strong>
          {task.payload.max_assignments ? money(task.payload.max_assignments * task.payload.reward_amount_minor) : "待填写名额"}
        </strong>
      </div>
    </div>
  );
}
function TaskContent({ task }: { task: Task }) {
  const p = task.payload;
  return (
    <div className="detail-grid">
      <div>
        <DetailSection title="任务说明">
          <div className="merchant-task-description"><MaterialImage value={p.icon_url} assetURLs={task.asset_urls} width={64} alt="任务图标" />
          <Typography.Paragraph style={{ whiteSpace: "pre-wrap" }}>
            {p.description || "—"}
          </Typography.Paragraph></div>
          <DetailFields
            column={{ xs: 1, sm: 2 }}
            size="small"
            items={[
              { key: "title", label: "任务名称", children: p.title },
              { key: "type", label: "任务类型", children: p.task_type === "message_send" ? "iMessage 任务" : typeLabels[p.task_type] },
              { key: "category", label: "任务分类", children: ({ experience: "体验任务", content: "内容任务", survey: "调研任务", onboarding: "新手任务", activity: "活动任务", verification: "核验任务" } as Record<string, string>)[p.category] || p.category },
              { key: "tags", label: "任务标签", children: p.tags?.join("、") || "—" },
              {
                key: "platform",
                label: "执行平台",
                children: {
                  ios: "iPhone / iOS",
                  web: "网页",
                  all: "iOS 与网页",
                }[p.platform],
              },
              {
                key: "start",
                label: "开始时间",
                children: dateTime(p.start_at),
              },
              { key: "end", label: "截止时间", children: dateTime(p.end_at) },
              {
                key: "timeout",
                label: "领取时限",
                children: `${p.assignment_timeout_seconds / 60} 分钟`,
              },
              {
                key: "screenshot",
                label: "完成截图",
                children: ["normal", "other_normal"].includes(p.task_type) ? `${p.completion_screenshot_count} 张` : "平台既定验证流程",
              },
            ]}
          />
        </DetailSection>
        <DetailSection title="任务步骤" style={{ marginTop: 20 }}>
          {!p.steps.length && <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="暂无任务步骤" />}
          <Timeline
            items={p.steps.map((s, i) => ({
              key: s.id,
              color: "blue",
              children: (
                <div className="task-step">
                  <span className="muted">步骤 {i + 1}</span>
                  <h4>{s.title}</h4>
                  <p style={{ whiteSpace: "pre-wrap" }}>{s.description}</p>
                  {s.links?.map((l, j) => (
                    <a
                      key={j}
                      href={safeUrl(l.url)}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      {l.label || l.url} <ArrowRightOutlined />
                    </a>
                  ))}
                  {s.images?.length > 0 && (
                    <Image.PreviewGroup>
                      <Space wrap>
                        {s.images.map((url, index) => <MaterialImage key={`${url}-${index}`} value={url} assetURLs={task.asset_urls} width={100} alt="任务步骤示例" />)}
                      </Space>
                    </Image.PreviewGroup>
                  )}
                </div>
              ),
            }))}
          />
        </DetailSection>
      </div>
      <div>
        <DetailSection title="提交与审核要求">
          {["normal", "other_normal"].includes(p.task_type) && p.required_screenshots?.map((shot, i) => <p key={`${shot.key}-${i}`}>第 {i + 1} 张 · {shot.title}（{shot.required ? "必填" : "选填"}）<br />{shot.description}</p>)}
          <h4>会员提交要求</h4>
          <p className="preserve-lines">{p.submission_requirements || "—"}</p>
          <h4>任务规则</h4>
          <p className="preserve-lines">{p.rules || "—"}</p>
          <h4>审核说明</h4>
          <p className="preserve-lines">{p.review_rules || "—"}</p>
        </DetailSection>
        {p.download_app && (
          <DetailSection title="下载应用信息" style={{ marginTop: 20 }}>
            <DetailFields
              column={1}
              items={[
                {
                  key: "name",
                  label: "应用名称",
                  children: p.download_app.name,
                },
                {
                  key: "id",
                  label: "App Store ID",
                  children: p.download_app.id,
                },
                {
                  key: "bundle",
                  label: "Bundle ID",
                  children: p.download_app.bundle_id,
                },
                {
                  key: "version",
                  label: "应用版本",
                  children: p.download_app.version,
                },
                {
                  key: "price", label: "应用售价", children: money(Math.round((p.download_app.price || 0) * 100)),
                },
                {
                  key: "developer",
                  label: "开发者",
                  children: p.download_app.developer_name,
                },
                {
                  key: "quotas", label: "关键词配额", children: p.download_app.search_keyword_quotas?.map(q => `${q.keyword}：${q.target_count} 份`).join("；") || "—",
                },
                {
                  key: "keyword",
                  label: "搜索词",
                  children: p.download_app.search_keywords?.join("、") || "—",
                },
              ]}
            />
          </DetailSection>
        )}
        {p.message_config && (
          <DetailSection title="收件人与发送内容" style={{ marginTop: 20 }}>
            {p.message_config.import_id && <MessageImport readOnly value={{ id: p.message_config.import_id, count: p.message_config.import_count || 0 }} />}
          </DetailSection>
        )}
        <DetailSection title="备注" style={{ marginTop: 20 }}>
          <p className="preserve-lines">{p.remarks || "—"}</p>
        </DetailSection>
      </div>
    </div>
  );
}
function safeUrl(url: string) {
  return /^https?:\/\//i.test(url) ? url : undefined;
}
function TaskHistory({ taskId, business }: { taskId: number; business: TaskBusiness }) {
  const revisions = useResource<{ items: Revision[] }>(
    `/tasks/${taskId}/revisions${query({ business })}`,
  );
  const reviews = useResource<{ items: Review[] }>(`/tasks/${taskId}/reviews${query({ business })}`);
  const [selected, setSelected] = useState<Revision>();
  return (
    <>
      <DetailSection title="修改记录" extra="查看每次修改与对应内容">
        <Resource resource={revisions}>
          {(data) => (
            <>
            <RevisionComparison revisions={data.items} />
            <List
              dataSource={data.items}
              locale={{ emptyText: "暂无修改记录" }}
              renderItem={(r) => (
                <List.Item
                  actions={[
                    <Button
                      type="link"
                      icon={<EyeOutlined />}
                      onClick={() => setSelected(r)}
                    >
                      查看当时内容
                    </Button>,
                  ]}
                >
                  <List.Item.Meta
                    avatar={
                      <div className="revision-icon">
                        <FileTextOutlined />
                      </div>
                    }
                    title={dateTime(r.created_at)}
                    description={
                      <>
                        {r.change_note || "创建任务"}
                        <br />
                        {dateTime(r.created_at)} · {revisionAuthor(r)}<br />
                        {money(r.payload.reward_amount_minor)} / 份 · {r.payload.max_assignments ? `${r.payload.max_assignments} 份` : "待填写名额"} · 预算 {r.payload.max_assignments ? money(r.payload.reward_amount_minor * r.payload.max_assignments) : "待填写名额"}
                      </>
                    }
                  />
                </List.Item>
              )}
            />
            </>
          )}
        </Resource>
      </DetailSection>
      <DetailSection title="平台审核记录">
        <Resource resource={reviews}>
          {(data) => data.items.length ? (
            <Timeline
              items={data.items.map((r) => ({
                color: r.decision === "approved" ? "green" : "red",
                children: (
                  <>
                    <strong>
                      {r.decision === "approved" ? "审核通过" : "审核驳回"}
                    </strong>
                    <span className="timeline-date">
                      {dateTime(r.created_at)}
                    </span>
                    <p>{r.note || "平台审核完成"}</p>
                    
                  </>
                ),
              }))}
            />
          ) : <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="暂无平台审核记录" />}
        </Resource>
      </DetailSection>
      <Modal
        className="merchant-detail-modal"
        title="历史任务内容"
        width={1000}
        open={!!selected}
        footer={<Button onClick={() => setSelected(undefined)}>关闭</Button>}
        onCancel={() => setSelected(undefined)}
      >
        {selected && (
          <DetailSurface><p className="merchant-detail-note">{revisionAuthor(selected)} · {dateTime(selected.created_at)}</p><RowSummary task={{ payload: selected.payload } as Task} /><TaskContent
            task={{
              id: taskId,
              agent_id: 0,
              version: selected.version || 0,
              current_revision_id: selected.id,
              status: "draft",
              payload: selected.payload,
              asset_urls: selected.asset_urls,
              created_at: selected.created_at,
              updated_at: selected.created_at,
            }}
          /></DetailSurface>
        )}
      </Modal>
    </>
  );
}

function LatestReview({ taskId, revisionId, business }: { taskId: number; revisionId: number; business: TaskBusiness }) {
  const resource = useResource<{ items: Review[] }>(`/tasks/${taskId}/reviews${query({ business })}`);
  if (resource.loading && !resource.data) return null;
  if (resource.error) return <Alert type="warning" message="审核原因加载失败" action={<Button onClick={() => void resource.reload()}>重试</Button>} style={{ marginBottom: 20 }} />;
  const review = resource.data?.items.find(r => r.revision_id === revisionId) || resource.data?.items[0];
  return review ? <Alert className="merchant-review-note" showIcon type={review.decision === "rejected" ? "warning" : "success"} message={`${review.decision === "rejected" ? "平台驳回原因" : "平台审核通过"}${review.revision_id !== revisionId ? "（此前审核结果）" : ""}`} description={<><p className="preserve-lines">{review.note || "平台审核完成"}</p><small>{dateTime(review.created_at)}</small></>} style={{ marginBottom: 20 }} /> : null;
}
function RevisionComparison({ revisions }: { revisions: Revision[] }) {
  const [beforeId, setBefore] = useState<number>();
  const [afterId, setAfter] = useState<number>();
  const ordered = [...revisions].sort((a, b) => (b.revision_no || b.version || b.id) - (a.revision_no || a.version || a.id));
  const before = revisions.find(r => r.id === beforeId) || ordered[1];
  const after = revisions.find(r => r.id === afterId) || ordered[0];
  if (!before || !after) return <p className="muted">暂无可比较的修改记录。</p>;
  const options = ordered.map(r => ({ value: r.id, label: dateTime(r.created_at) }));
  return <div style={{ marginBottom: 24 }}><Space wrap style={{ marginBottom: 16 }}><span>修改前</span><Select aria-label="修改前内容" value={before.id} options={options} onChange={setBefore} style={{ minWidth: 225 }} /><span>修改后</span><Select aria-label="修改后内容" value={after.id} options={options} onChange={setAfter} style={{ minWidth: 225 }} /></Space><TaskDiff before={before} after={after} /></div>;
}
